import React, { useMemo, useState } from 'react';
import { Alert, Button, Row } from 'antd';
import {
  ComplianceStateLegend,
  ComplianceItemTables,
} from '@onboarding/components';
import { Icon } from '@common/components';
import { CandidateAssignmentItemAddModal } from 'works/components/candidate-assignment/candidate-assignment-item-add-modal';
import { CandidateAssignmentItemEditModal } from 'works/components/candidate-assignment/candidate-assignment-item-edit-modal';
import { OnboardingListEditActions } from 'works/components/candidate-assignment/onboarding-list-edit-actions';
import { WorksListItemType, WorksListType } from '@onboarding/graphql';

type Props = {
  onCancel: () => void;
  onSave: () => void;
  hasComplianceTemplate: boolean;
  worksList: WorksListType;
};

export function ComplianceItemApprovalListEdition({
  worksList,
  onCancel,
  onSave,
  hasComplianceTemplate,
}: Props) {
  const { id: worksListId } = worksList;

  const [showAddModal, setShowAddModal] = useState(false);
  const [editingItem, setEditingItem] = useState<WorksListItemType | null>();
  const [itemsChanged, setItemsChanged] = useState<WorksListItemType[]>([]);
  const [itemsAdded, setItemsAdded] = useState<WorksListItemType[]>([]);

  const worksListItems = useMemo(() => {
    const list = worksList.worksListItems.concat(itemsAdded);

    itemsChanged.forEach((item) => {
      const index = list.findIndex((i) => i.id === item.id);

      if (index !== -1) {
        list[index] = item;
      }
    });

    return [...list].sort((a, b) => a.name.localeCompare(b.name));
  }, [worksList, itemsChanged, itemsAdded]);

  const onAssignmentItemAdded = (
    values: Omit<WorksListItemType, 'id' | 'type'>,
    type: string
  ) => {
    setShowAddModal(false);
    // Give unique id to the added item so it can be identified when editing
    const id = new Date().getTime().toString();
    setItemsAdded([...itemsAdded, { id, type, ...values }]);
  };

  const onAssignmentItemEdited = (
    values: Omit<WorksListItemType, 'id' | 'type'>,
    type: string,
    id: string
  ) => {
    const workItem = { id, type, ...values };
    // Check if it's an added or changed item
    const addedIndex = itemsAdded.findIndex((item) => item.id === id);
    const changedIndex = itemsChanged.findIndex((item) => item.id === id);
    if (addedIndex !== -1) {
      const copy = [...itemsAdded];
      copy[addedIndex] = workItem;
      setItemsAdded(copy);
    } else if (changedIndex !== -1) {
      const copy = [...itemsChanged];
      copy[changedIndex] = workItem;
      setItemsChanged(copy);
    } else {
      setItemsChanged([...itemsChanged, workItem]);
    }
    setEditingItem(null);
  };

  const actionColumns = [
    {
      title: 'EDIT',
      dataIndex: '',
      key: 'edit',
      onHeaderCell: () => ({ style: { textAlign: 'right', width: '180px' } }),
      onCell: () => ({ style: { textAlign: 'right' } }),
      render: (listItem: WorksListItemType) => (
        <a onClick={() => setEditingItem(listItem)}>
          <Icon icon="pencil" />
        </a>
      ),
    },
  ];

  return (
    <>
      {showAddModal && (
        <CandidateAssignmentItemAddModal
          dismiss={() => setShowAddModal(false)}
          handleSubmit={onAssignmentItemAdded}
        />
      )}
      {editingItem && (
        <CandidateAssignmentItemEditModal
          item={editingItem}
          dismiss={() => setEditingItem(null)}
          handleSubmit={onAssignmentItemEdited}
        />
      )}

      <Row justify="space-between" align="middle">
        {hasComplianceTemplate && (
          <Alert
            showIcon
            style={{ width: '100%' }}
            icon={<Icon icon="exclamationTriangle" kind="solid" size="sm" />}
            message={
              <>
                This compliance list will not get any future updates from the
                compliance template once any changes are saved.
              </>
            }
            type="warning"
          />
        )}
        <ComplianceStateLegend />
        <Button onClick={() => setShowAddModal(true)}>
          + Add Compliance Item
        </Button>
      </Row>
      <section>
        <ComplianceItemTables
          worksList={{ ...worksList, worksListItems }}
          actionColumns={actionColumns}
        />
        <OnboardingListEditActions
          onCancel={onCancel}
          onSave={onSave}
          itemsAdded={itemsAdded}
          itemsChanged={itemsChanged}
          worksListId={worksListId}
        />
      </section>
    </>
  );
}
